import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "./firebaseConfig";
import type { KioskOperationMode } from "./utils/kioskOperation";

export interface KioskType {
    id: string;
    docId: string;
    status: "PENDING" | "ACTIVE" | "INACTIVE" | "FULL";
    name?: string;
    address?: string;
    lat?: number;
    lng?: number;
    maxCapacity?: number;
    currentCapacity?: number;
    message?: string;
    photo?: string;
    timestamp?: number;
    partnerId?: string;
    operationMode?: KioskOperationMode;
    collectionCycleId?: string;
    lastEmptiedAt?: number;
}

interface KioskContextType {
    kiosks: KioskType[];
    pendings: KioskType[];
}

const KioskContext = createContext<KioskContextType | null>(null);

export const KioskProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const [kiosks, setKiosks] = useState<KioskType[]>([]);
    const [pendings, setPendings] = useState<KioskType[]>([]);

    useEffect(() => {
        const unsubscribe = onSnapshot(query(collection(db, "kiosk"), where("status", "==", "PENDING")), (snapshot) => {
            const kioskLists = snapshot.docs
                .map((doc) => ({ ...doc.data(), docId: doc.id } as KioskType))
                .sort((a, b) => {
                    if (a.timestamp && b.timestamp) return a.timestamp - b.timestamp;
                    else return 0;
                });
            setPendings(kioskLists);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const unsubscribe = onSnapshot(query(collection(db, "kiosk"), where("status", "!=", "PENDING")), (snapshot) => {
            const kioskLists = snapshot.docs
                .map((doc) => ({ ...doc.data(), docId: doc.id } as KioskType))
                .sort((a, b) => {
                    if (a.timestamp && b.timestamp) return a.timestamp - b.timestamp;
                    else return 0;
                });
            setKiosks(kioskLists);
        });
        return () => unsubscribe();
    }, []);

    return (
        <KioskContext.Provider
            value={{
                kiosks,
                pendings,
            }}
        >
            {children}
        </KioskContext.Provider>
    );
};

export const useKiosk = (): KioskContextType => {
    const context = useContext(KioskContext);
    if (!context) {
        throw new Error("useKiosk must be used within an KioskProvider");
    }
    return context;
};
